import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useState } from 'react'

export type Option = { value: string | number; label: string }

type State = { open: false } | { open: true; dropdownId: string; activeDescendent: Option }

const DropdownContext = createContext({
  state: { open: false } as State,
  setState: (() => null) as Dispatch<SetStateAction<State>>,
})

export const DatePickerDropdownContextProvider = ({
  children,
}: {
  children: (options: { close: () => void }) => ReactNode
}) => {
  const [state, setState] = useState<State>({ open: false })
  return (
    <DropdownContext.Provider value={{ state, setState }}>
      {children({ close: () => setState({ open: false }) })}
    </DropdownContext.Provider>
  )
}

export const useDropdownStatus = () => {
  const { state } = useContext(DropdownContext)
  return { open: state.open }
}

type CurrentDropdownState = { open: false } | { open: true; activeDescendent: Option }

export const useDropdownState = (dropdownId: string) => {
  const { state, setState } = useContext(DropdownContext)

  const open = (params: { on: 'first' | 'last'; options: Option[] } | { on: 'selected'; selected: Option }) => {
    switch (params.on) {
      case 'selected':
        setState({ open: true, dropdownId, activeDescendent: params.selected })
        break
      case 'first':
        setState({ open: true, dropdownId, activeDescendent: params.options[0] })
        break
      case 'last':
        setState({ open: true, dropdownId, activeDescendent: params.options[params.options.length - 1] })
        break
    }
  }

  const activate = ({ direction, options }: { direction: 'previous' | 'next'; options: Option[] }) => {
    setState(state => {
      if (state.open && state.dropdownId === dropdownId) {
        const currentIndex = options.findIndex(option => option.value === state.activeDescendent.value)
        const nextIndex = (direction === 'next' ? currentIndex + 1 : currentIndex - 1 + options.length) % options.length
        return { ...state, activeDescendent: options[nextIndex] }
      }
      return state
    })
  }

  const close = () => {
    setState({ open: false })
  }

  const currentState: CurrentDropdownState =
    state.open && state.dropdownId === dropdownId
      ? { open: true, activeDescendent: state.activeDescendent }
      : { open: false }

  return {
    open,
    close,
    activate,
    state: currentState,
  }
}
